import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import '../App.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Use relative URL for API calls in Docker, fallback to localhost for local dev
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ClaudeDashboard = () => {
  const history = useHistory();
  const [summary, setSummary] = useState(null);
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [modelMetrics, setModelMetrics] = useState([]);
  const [costTrends, setCostTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [days, setDays] = useState(7);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, dailyRes, modelRes, costRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/metrics/summary?days=${days}`),
        axios.get(`${API_BASE_URL}/metrics/daily?days=30`),
        axios.get(`${API_BASE_URL}/metrics/by-model?days=${days}`),
        axios.get(`${API_BASE_URL}/metrics/cost-trends?days=30`),
      ]);

      setSummary(summaryRes.data);
      setDailyMetrics(dailyRes.data.data);
      setModelMetrics(modelRes.data.data);
      setCostTrends(costRes.data.data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncStatus({ type: 'info', message: 'Sync started...' });

      await axios.post(`${API_BASE_URL}/sync?days=30`, {});

      // Wait for sync to complete
      setTimeout(async () => {
        try {
          // Fetch updated summary to check for data
          const summaryRes = await axios.get(`${API_BASE_URL}/metrics/summary?days=7`);
          const recordCount = summaryRes.data.total_sessions || 0;

          setLastSyncTime(new Date());

          if (recordCount === 0) {
            setSyncStatus({
              type: 'warning',
              message: `Sync completed successfully, but no usage data found. The API returned 0 records for the last 30 days. This could mean: (1) No Claude usage yet, (2) Usage data not yet available in Anthropic's systems, or (3) Organization account not properly configured.`,
              details: `Synced at: ${new Date().toLocaleString()}`
            });
          } else {
            setSyncStatus({
              type: 'success',
              message: `Sync completed successfully! Found ${recordCount} sessions.`,
              details: `Synced at: ${new Date().toLocaleString()}`
            });
          }

          await fetchData();
        } catch (err) {
          setSyncStatus({
            type: 'error',
            message: `Sync initiated but failed to fetch results: ${err.message}`,
            details: err.response?.data ? JSON.stringify(err.response.data) : null
          });
        } finally {
          setSyncing(false);
        }
      }, 5000);
    } catch (err) {
      setSyncing(false);
      setSyncStatus({
        type: 'error',
        message: `Failed to start sync: ${err.message}`,
        details: err.response?.data ? JSON.stringify(err.response.data) : null
      });
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [days]);

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading Claude Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const tokenTrendData = {
    labels: dailyMetrics.map((m) => m.date),
    datasets: [
      {
        label: 'Input Tokens',
        data: dailyMetrics.map((m) => m.input_tokens),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Output Tokens',
        data: dailyMetrics.map((m) => m.output_tokens),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
      {
        label: 'Cache Read Tokens',
        data: dailyMetrics.map((m) => m.cache_read_tokens || 0),
        borderColor: 'rgb(255, 205, 86)',
        backgroundColor: 'rgba(255, 205, 86, 0.2)',
      },
    ],
  };

  const costTrendData = {
    labels: costTrends.map((m) => m.date),
    datasets: [
      {
        label: 'Cost (USD)',
        data: costTrends.map((m) => m.cost_usd),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        fill: true,
      },
    ],
  };

  const modelCostData = {
    labels: modelMetrics.map((m) => m.model),
    datasets: [
      {
        label: 'Cost (USD)',
        data: modelMetrics.map((m) => m.cost_usd),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
      },
    ],
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-left">
          <button onClick={() => history.push('/main_app')} className="back-btn" title="Back to Dashboard Selection">
            ← Back
          </button>
          <h1>
            <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>🤖</span>
            Claude Dashboard
          </h1>
        </div>
        <div className="header-controls">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="days-selector"
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', marginRight: '8px' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={handleSync} disabled={syncing} className="sync-btn">
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>

        </div>
      </header>

      {summary && (
        <div className="summary-cards">
          <div className="card">
            <h3>Total Cost</h3>
            <p className="metric-value">${summary.total_cost_usd.toFixed(2)}</p>
            <p className="metric-label">Last {days} days</p>
          </div>
          <div className="card">
            <h3>Total Tokens</h3>
            <p className="metric-value">
              {(summary.total_input_tokens + summary.total_output_tokens).toLocaleString()}
            </p>
            <p className="metric-label">Input + Output</p>
          </div>
          <div className="card">
            <h3>Sessions</h3>
            <p className="metric-value">{summary.total_sessions.toLocaleString()}</p>
            <p className="metric-label">Last {days} days</p>
          </div>
          <div className="card">
            <h3>Active Users</h3>
            <p className="metric-value">{summary.unique_users}</p>
            <p className="metric-label">Unique developers</p>
          </div>
          <div className="card">
            <h3>Cache Read Tokens</h3>
            <p className="metric-value">{(summary.total_cache_read_tokens || 0).toLocaleString()}</p>
            <p className="metric-label">Cached tokens</p>
          </div>
          <div className="card">
            <h3>Avg Cost/Session</h3>
            <p className="metric-value">
              ${summary.total_sessions > 0 ? (summary.total_cost_usd / summary.total_sessions).toFixed(4) : '0.00'}
            </p>
            <p className="metric-label">Per session</p>
          </div>
        </div>
      )}

      <div className="charts-container">
        <div className="chart-card">
          <h2>Token Usage Trends (30 Days)</h2>
          <Line data={tokenTrendData} options={{ responsive: true, maintainAspectRatio: true }} />
        </div>

        <div className="chart-card">
          <h2>Cost Trends (30 Days)</h2>
          <Line
            data={costTrendData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function (value) {
                      return '$' + value.toFixed(2);
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="chart-card">
          <h2>Cost by Model (Last {days} Days)</h2>
          <Doughnut
            data={modelCostData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  position: 'right',
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return context.label + ': $' + context.parsed.toFixed(2);
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="chart-card full-width">
          <h2>Model Usage Details (Last {days} Days)</h2>
          {modelMetrics.length > 0 ? (
            <div className="table-container">
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Sessions</th>
                    <th>Input Tokens</th>
                    <th>Output Tokens</th>
                    <th>Cache Read Tokens</th>
                    <th>Total Tokens</th>
                    <th>Cost (USD)</th>
                    <th>Avg Cost/Session</th>
                  </tr>
                </thead>
                <tbody>
                  {modelMetrics.map((model, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{model.model}</strong>
                      </td>
                      <td>{model.sessions.toLocaleString()}</td>
                      <td>{model.input_tokens.toLocaleString()}</td>
                      <td>{model.output_tokens.toLocaleString()}</td>
                      <td>{(model.cache_read_tokens || 0).toLocaleString()}</td>
                      <td>{(model.input_tokens + model.output_tokens).toLocaleString()}</td>
                      <td>
                        <strong>${model.cost_usd.toFixed(2)}</strong>
                      </td>
                      <td>
                        ${model.sessions > 0 ? (model.cost_usd / model.sessions).toFixed(4) : '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No model usage data available yet.</p>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                Click "Sync Data" to fetch your Claude usage data.
              </p>
            </div>
          )}
        </div>
      </div>

      {syncStatus && (
        <div className={`status-footer status-${syncStatus.type}`}>
          <div className="status-content">
            <div className="status-icon">
              {syncStatus.type === 'success' && '✓'}
              {syncStatus.type === 'error' && '✗'}
              {syncStatus.type === 'warning' && '⚠'}
              {syncStatus.type === 'info' && 'ℹ'}
            </div>
            <div className="status-message">
              <strong>{syncStatus.message}</strong>
              {syncStatus.details && <div className="status-details">{syncStatus.details}</div>}
            </div>
            <button className="status-close" onClick={() => setSyncStatus(null)}>×</button>
          </div>
        </div>
      )}

      <footer className="footer">
        <p>
          Data refreshed every 5 minutes | Last updated: {new Date().toLocaleString()}
          {lastSyncTime && ` | Last sync: ${lastSyncTime.toLocaleString()}`}
        </p>
      </footer>
    </div>
  );
};

export default ClaudeDashboard;
